import { useMemo, useState } from 'react';
import { clsx } from 'clsx';
import { Button, ConfirmDialog } from '@/shared/ui';
import { Modal } from '@/shared/ui/Modal';
import { useToast } from '@/shared/ui/Toast';
import type { AgentFact, AgentFactKind } from '@/modules/agents/types';
import { relativeTime } from '@/modules/agents/utils/agentStatusLabels';
import { useClientFacts, useRetireClientFacts } from '../../hooks/useCommandCenter';
import styles from './AgentStatus.module.css';

const KIND_LABEL: Record<AgentFactKind, string> = {
  fact: 'Fact',
  preference: 'Preference',
  correction: 'Correction',
};

/**
 * The client facts at one location, over the agent tab: what the practice has told its agents, read
 * into every later task there. Every agent at the location shares it. Select facts (or all) to
 * remove them; the agents stop reading them. Removal is recorded in the audit log.
 */
export function ClientFactsPanel({
  open,
  onClose,
  practiceId,
  locationId,
  locationName,
}: {
  open: boolean;
  onClose: () => void;
  practiceId: string;
  locationId: string;
  locationName: string;
}) {
  const toast = useToast();
  const query = useClientFacts(practiceId, locationId, open);
  const retire = useRetireClientFacts(practiceId, locationId);
  const facts = useMemo(() => query.data ?? [], [query.data]);
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [confirming, setConfirming] = useState(false);
  // Only ids still in the list count: a fact removed elsewhere drops out of the selection.
  const chosen = facts.filter((fact) => selected.has(fact.id));
  const allChosen = facts.length > 0 && chosen.length === facts.length;
  const now = Date.now();

  const toggle = (id: string) =>
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const toggleAll = () =>
    setSelected(allChosen ? new Set() : new Set(facts.map((fact) => fact.id)));

  const remove = async () => {
    try {
      const { retired } = await retire.mutateAsync(chosen.map((fact) => fact.id));
      toast.success(`Removed ${retired} ${retired === 1 ? 'fact' : 'facts'} from client facts`);
      setSelected(new Set());
      setConfirming(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not remove these facts');
    }
  };

  return (
    <Modal isOpen={open} onClose={onClose} title="Client facts" variant="side" size="xl">
      <div className={styles.runLog}>
        <p className={styles.quiet}>
          What the practice has told the agents at {locationName}. Every agent here reads these in
          every task. Remove anything wrong or out of date.
        </p>
        {query.isLoading && <p className={styles.quiet}>Loading client facts…</p>}
        {query.isError && <p className={styles.quiet}>Client facts could not be loaded.</p>}
        {query.data && facts.length === 0 && <p className={styles.quiet}>No client facts yet.</p>}
        {facts.length > 0 && (
          <>
            <div className={styles.factsToolbar}>
              <label className={styles.toggle}>
                <input type="checkbox" checked={allChosen} onChange={toggleAll} />
                {allChosen ? 'Clear selection' : `Select all (${facts.length})`}
              </label>
              <Button
                variant="destructive"
                size="sm"
                disabled={chosen.length === 0 || retire.isPending}
                onClick={() => setConfirming(true)}
              >
                {chosen.length > 0 ? `Delete selected (${chosen.length})` : 'Delete selected'}
              </Button>
            </div>
            <ul className={styles.factsList}>
              {facts.map((fact) => (
                <ClientFactItem
                  key={fact.id}
                  fact={fact}
                  now={now}
                  checked={selected.has(fact.id)}
                  onToggle={() => toggle(fact.id)}
                />
              ))}
            </ul>
          </>
        )}
      </div>
      <ConfirmDialog
        isOpen={confirming}
        onClose={() => setConfirming(false)}
        onConfirm={remove}
        title={`Delete ${chosen.length} ${chosen.length === 1 ? 'fact' : 'facts'}?`}
        message="The agents at this location stop reading them in every task from now on. The removal is recorded in the audit log."
        confirmLabel="Delete"
        variant="destructive"
        isLoading={retire.isPending}
      />
    </Modal>
  );
}

function ClientFactItem({
  fact,
  now,
  checked,
  onToggle,
}: {
  fact: AgentFact;
  now: number;
  checked: boolean;
  onToggle: () => void;
}) {
  return (
    <li className={clsx(styles.factItem, checked && styles.factItemChecked)}>
      <label className={styles.factLabel}>
        <input type="checkbox" checked={checked} onChange={onToggle} />
        <span className={styles.factBody}>
          <span className={styles.factText}>{fact.text}</span>
          <span className={styles.runLine}>
            <span className={styles.toneMuted}>{KIND_LABEL[fact.kind]}</span>
            <span title={new Date(fact.createdAt).toLocaleString()}>
              Added {relativeTime(fact.createdAt, now)}
            </span>
          </span>
        </span>
      </label>
    </li>
  );
}
